import {
  Alert,
  Box,
  Button,
  Divider,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ServerIcon,
  Spinner,
  TextArea,
  TextInput,
} from '@razorpay/blade/components';
import gradientGray from 'assets/mss/gradient-gray.svg';
import { FieldGroup } from 'components/WizardViews/components';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import { useFormWithProvider } from 'forms';
import React from 'react';

import PreviewSelectedStructure from './components/PreviewSelectedStructure';
import { useListSalaryStructures } from '../hooks';
import { SalaryStructureFormData } from './types';
import { basicDetailsStepValidationSchema } from './utils';

import SalaryStructureDropDown from './components/SalaryStructureDropDown';
import { AppError } from 'utils/AppError';
import { SalaryStructureIntroInformation } from './components/SalaryStructureIntroInformation';

const BasicDetailsStep = ({
  isFetchingTemplate,
  refetchTemplate,
  templateError,
}: {
  isFetchingTemplate: boolean;
  refetchTemplate: () => void;
  templateError: AppError | undefined;
}) => {
  const { updateStepStatus, setCurrentStep, closeWizard } = useWizardInfo();
  const { register, validate, formData } = useFormWithProvider<SalaryStructureFormData>();

  const isCreationTypeFromTemplate = formData.creationType === 'from-existing';

  const { data, isFetching, error, refetch } = useListSalaryStructures({
    enabled: isCreationTypeFromTemplate,
  });

  const templates = data ?? [];

  const handleNextClick = () => {
    if (validate(basicDetailsStepValidationSchema)) {
      updateStepStatus(0, 'success');
      setCurrentStep(1);
    }
  };

  const handleBackClick = () => {
    closeWizard();
  };

  const disableNextCta = isFetching || !!error || !!templateError || isFetchingTemplate;
  const isEditOrTemplateMode = formData.isEditTemplateMode || isCreationTypeFromTemplate

  return (
    <>
      <ModalHeader
        leading={<ServerIcon color='interactive.icon.positive.normal' />}
        title={formData.isEditTemplateMode ? 'Edit structure' : 'Create structure'}
        subtitle='Give structure name and description'
      />
      <ModalBody height="100%">
        {error || !!templateError ? (
          <Alert
            color="negative"
            title="Error"
            description={!!error ? (error as AppError).message : templateError?.message || ''}
            actions={{
              primary: {
                text: 'Retry',
                onClick: !!error ? refetch : refetchTemplate,
              },
            }}
          />
        ) : ((isEditOrTemplateMode) && isFetchingTemplate) ||
          isFetching ? (
          <Box
            width={'100vw'}
            height={'100%'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}>
            <Spinner accessibilityLabel="loading template" size="xlarge" />
          </Box>
        ) : (
          <Box
            display="flex"
            gap="spacing.11"
            height="100%"
            backgroundImage={`url("${gradientGray}")`}
            backgroundRepeat="no-repeat"
            backgroundSize="contain"
            backgroundPosition="right bottom">
            <Box flex="1">
              <FieldGroup title="General details">
                <TextInput
                  necessityIndicator="required"
                  label="Structure name"
                  placeholder="Structure name"
                  {...register('name')}
                />
                <TextArea
                  necessityIndicator="required"
                  label="Description"
                  placeholder="Description"
                  {...register('description')}
                />
              </FieldGroup>

              {isCreationTypeFromTemplate && (
                <>
                  <Divider marginY="spacing.8" />

                  <FieldGroup title="Select template">
                    <SalaryStructureDropDown
                      structures={templates}
                      registerProps={register('templateId', 'select')}
                    />
                  </FieldGroup>
                </>
              )}
            </Box>
            <Box height="100%">
              <PreviewSelectedStructure isPreviewHidden={!isCreationTypeFromTemplate} />
            </Box>

            {!(isEditOrTemplateMode) ? (
              <Box height="100%">
                <SalaryStructureIntroInformation />
              </Box>
            ) : null}
          </Box>
        )}
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="space-between">
          <Button variant="tertiary" onClick={handleBackClick} isDisabled={isFetching}>
            Back: All templates
          </Button>
          <Button onClick={handleNextClick} isDisabled={disableNextCta}>
            Next
          </Button>
        </Box>
      </ModalFooter>
    </>
  );
};

export default BasicDetailsStep;
